// TelcoTrack — Formulářové pole účastníků smlouvy dle typu dokumentu.
// Jediný zdroj pravdy pro web / Android: co se ukáže v „Údaje smlouvy".
// Odvozeno od toho, co HTML šablony skutečně interpolují (viz komentáře u typů).
// Prefill KN dál plní celý snapshot; UI zobrazí jen relevantní pole.

import type { AcquisitionContractType } from './acquisition-contract.constants';
import {
  ACQUISITION_CONTRACT_TYPES,
  isAcquisitionSingleBuildingParcelContract,
} from './acquisition-contract.constants';
import {
  CONTRACT_PARTY_NP_HINT_CS,
  CONTRACT_PARTY_NP_PLACEHOLDER_CS,
} from './object-workflow/digitalni-cesta-spec.constants';

/** Editovatelná pole vlastníka ve formuláři (podmnožina snapshotu). */
export const CONTRACT_PARTY_OWNER_FORM_FIELDS = [
  'jmenoNazev',
  'ico',
  'adresa',
  'podil',
  'email',
  'telefon',
  'zastupci',
  'dic',
  'spisovaZnacka',
  'bankovniUcet',
  'datumNarozeni',
  'np',
  /** Toggle Schválena / Odmítnuta — UI renderuje tlačítka, ne text input. */
  'digitalniCestaStav',
  /** Volitelná poznámka k schválení / odmítnutí digitální cesty. */
  'digitalniCestaPoznamka',
] as const;

export type ContractPartyOwnerFormField =
  (typeof CONTRACT_PARTY_OWNER_FORM_FIELDS)[number];

/** Editovatelná pole parcely ve formuláři (podmnožina snapshotu). */
export const CONTRACT_PARTY_PARCEL_FORM_FIELDS = [
  'parcelniCislo',
  'kuNazev',
  'obec',
  'ulice',
  'popisneCislo',
  'orientacniCislo',
  'lv',
  'vymera',
  'druhPozemku',
] as const;

export type ContractPartyParcelFormField =
  (typeof CONTRACT_PARTY_PARCEL_FORM_FIELDS)[number];

export interface ContractDocumentPartyFieldSpec<K extends string> {
  key: K;
  /** Povinné pro smysluplný dokument (validace UI / budoucí API). */
  required: boolean;
  /** České UI label (volitelné — klienti mohou mít vlastní). */
  labelCs: string;
  /** Krátká nápověda pod polem (volitelné). */
  hintCs?: string;
  /** Placeholder v inputu (volitelné). */
  placeholderCs?: string;
  /** HTML / soft keyboard hint — např. `numeric` pro číslo patra. */
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
}

export interface ContractDocumentPartyFieldsSchema {
  contractType: AcquisitionContractType;
  /**
   * Klíče šablon, které tato sada pokrývá (dokumentace + testy).
   * Souhlas NGA/default sdílí stejný form schema.
   */
  templateKeys: readonly string[];
  /**
   * Pole, která HTML šablona čte z vlastníka (včetně server-filled hodnot,
   * které UI nemusí editovat — např. historicky `np` před zařazením do formuláře).
   */
  templateOwnerFields: readonly string[];
  /** Pole, která HTML šablona čte z parcely. */
  templateParcelFields: readonly string[];
  /** Pole zobrazená ve formuláři „Údaje smlouvy" (vlastníci + zástupci). */
  owners: readonly ContractDocumentPartyFieldSpec<ContractPartyOwnerFormField>[];
  /** Pole zobrazená ve formuláři (parcely). */
  parcels: readonly ContractDocumentPartyFieldSpec<ContractPartyParcelFormField>[];
  /** Nápověda „pro digitální podpis vyplňte e-mail nebo telefon". */
  showDigitalSignContactHint: boolean;
}

const NEMOVITOSTI_PARCEL_FORM: readonly ContractDocumentPartyFieldSpec<ContractPartyParcelFormField>[] =
  [
    { key: 'parcelniCislo', required: true, labelCs: 'Parcelní číslo' },
    { key: 'kuNazev', required: false, labelCs: 'K.ú.' },
    { key: 'obec', required: false, labelCs: 'Obec' },
    { key: 'ulice', required: false, labelCs: 'Ulice' },
    { key: 'popisneCislo', required: false, labelCs: 'Č.p.' },
    { key: 'orientacniCislo', required: false, labelCs: 'Č.or.' },
  ];

/** Nemovitosti section (souhlas + digitalni_cesta) — bez LV / výměry / druhu. */
const NEMOVITOSTI_TEMPLATE_PARCEL_FIELDS = [
  'kuNazev',
  'obec',
  'ulice',
  'parcelniCislo',
  'popisneCislo',
  'orientacniCislo',
] as const;

/**
 * Souhlas (NGA `renderOwnerBlock` + default `renderOwnersIdTable` / contacts / signatures).
 * Form ukazuje praktickou sadu k editaci; KN může ve snapshotu držet i dic / datumNarozeni /
 * spisovaZnacka (šablona je vypíše, pokud jsou), UI je neptá.
 */
const SOUHLAS_SCHEMA: ContractDocumentPartyFieldsSchema = {
  contractType: 'souhlas',
  templateKeys: ['souhlas:nga', 'souhlas:default'],
  templateOwnerFields: [
    'jmenoNazev',
    'adresa',
    'datumNarozeni',
    'ico',
    'dic',
    'spisovaZnacka',
    'zastupci',
    'podil',
    'email',
    'telefon',
  ],
  templateParcelFields: [...NEMOVITOSTI_TEMPLATE_PARCEL_FIELDS],
  owners: [
    { key: 'jmenoNazev', required: true, labelCs: 'Jméno / název' },
    { key: 'podil', required: false, labelCs: 'Podíl' },
    { key: 'ico', required: false, labelCs: 'IČO' },
    { key: 'adresa', required: false, labelCs: 'Adresa' },
    { key: 'email', required: false, labelCs: 'E-mail' },
    { key: 'telefon', required: false, labelCs: 'Telefon' },
    { key: 'zastupci', required: false, labelCs: 'Jméno' },
  ],
  parcels: NEMOVITOSTI_PARCEL_FORM,
  showDigitalSignContactHint: true,
};

/**
 * Protokol digitální cesty — vlastník: jméno, číslo patra (`np`), adresa, IČO,
 * e-mail / telefon (sign gate: stačí u jedné osoby), zástupci,
 * stav Schválena/Odmítnuta (`digitalniCestaStav` → BJ „souhlasí"/„nesouhlasí"),
 * volitelná poznámka (`digitalniCestaPoznamka` → BJ řádek).
 * Kontakt do HTML nemusí (formulář ano). `np` se předvyplní z pater HP (jen číslo).
 */
const DIGITALNI_CESTA_SCHEMA: ContractDocumentPartyFieldsSchema = {
  contractType: 'digitalni_cesta',
  templateKeys: ['digitalni_cesta'],
  templateOwnerFields: [
    'jmenoNazev',
    'np',
    'digitalniCestaStav',
    'digitalniCestaPoznamka',
    'adresa',
    'ico',
    'email',
    'telefon',
    'zastupci',
  ],
  templateParcelFields: [...NEMOVITOSTI_TEMPLATE_PARCEL_FIELDS],
  owners: [
    { key: 'jmenoNazev', required: true, labelCs: 'Jméno / název' },
    {
      key: 'np',
      required: false,
      labelCs: 'Číslo patra',
      hintCs: CONTRACT_PARTY_NP_HINT_CS,
      placeholderCs: CONTRACT_PARTY_NP_PLACEHOLDER_CS,
      inputMode: 'numeric',
    },
    { key: 'digitalniCestaStav', required: true, labelCs: 'Digitální cesta' },
    { key: 'digitalniCestaPoznamka', required: false, labelCs: 'Poznámka' },
    { key: 'ico', required: false, labelCs: 'IČO' },
    { key: 'adresa', required: false, labelCs: 'Adresa' },
    { key: 'email', required: false, labelCs: 'E-mail' },
    { key: 'telefon', required: false, labelCs: 'Telefon' },
    { key: 'zastupci', required: false, labelCs: 'Jméno' },
  ],
  parcels: NEMOVITOSTI_PARCEL_FORM,
  showDigitalSignContactHint: true,
};

/**
 * Placeholder vstup / předávací — `renderPartiesSection` + `renderParcelsTable` (včetně LV).
 * Připraveno pro budoucí nabídku v UI; zatím katalog-only.
 * Kontakt per osoba (stejná sign-gate pravidla jako u souhlasu / DC).
 */
const PLACEHOLDER_FULL_PARTY_FORM: readonly ContractDocumentPartyFieldSpec<ContractPartyOwnerFormField>[] =
  [
    { key: 'jmenoNazev', required: true, labelCs: 'Jméno / název' },
    { key: 'podil', required: false, labelCs: 'Podíl' },
    { key: 'ico', required: false, labelCs: 'IČO' },
    { key: 'dic', required: false, labelCs: 'DIČ' },
    { key: 'adresa', required: false, labelCs: 'Adresa' },
    { key: 'email', required: false, labelCs: 'E-mail' },
    { key: 'telefon', required: false, labelCs: 'Telefon' },
    { key: 'spisovaZnacka', required: false, labelCs: 'Spisová značka' },
    { key: 'bankovniUcet', required: false, labelCs: 'Bankovní účet' },
    { key: 'zastupci', required: false, labelCs: 'Jméno' },
  ];
const PLACEHOLDER_PARCEL_FORM: readonly ContractDocumentPartyFieldSpec<ContractPartyParcelFormField>[] =
  [
    { key: 'parcelniCislo', required: true, labelCs: 'Parcelní číslo' },
    { key: 'kuNazev', required: false, labelCs: 'K.ú.' },
    { key: 'obec', required: false, labelCs: 'Obec' },
    { key: 'lv', required: false, labelCs: 'LV' },
    { key: 'druhPozemku', required: false, labelCs: 'Druh pozemku' },
    { key: 'vymera', required: false, labelCs: 'Výměra' },
  ];

const VSTUP_SCHEMA: ContractDocumentPartyFieldsSchema = {
  contractType: 'vstup_na_pozemek',
  templateKeys: ['vstup_na_pozemek'],
  templateOwnerFields: [
    'jmenoNazev',
    'adresa',
    'podil',
    'ico',
    'dic',
    'email',
    'telefon',
    'spisovaZnacka',
    'zastupci',
    'bankovniUcet',
  ],
  templateParcelFields: [
    'kuNazev',
    'obec',
    'parcelniCislo',
    'lv',
    'druhPozemku',
    'vymera',
  ],
  owners: PLACEHOLDER_FULL_PARTY_FORM,
  parcels: PLACEHOLDER_PARCEL_FORM,
  showDigitalSignContactHint: true,
};

const PREDAVACI_SCHEMA: ContractDocumentPartyFieldsSchema = {
  ...VSTUP_SCHEMA,
  contractType: 'predavaci_protokol',
  templateKeys: ['predavaci_protokol'],
};

/**
 * Katalog schémat per `AcquisitionContractType`.
 * Při novém typu smlouvy: doplnit záznam + test oproti šabloně.
 */
export const CONTRACT_DOCUMENT_PARTY_FIELDS_BY_TYPE: Record<
  AcquisitionContractType,
  ContractDocumentPartyFieldsSchema
> = {
  souhlas: SOUHLAS_SCHEMA,
  digitalni_cesta: DIGITALNI_CESTA_SCHEMA,
  vstup_na_pozemek: VSTUP_SCHEMA,
  predavaci_protokol: PREDAVACI_SCHEMA,
};

/** Výchozí schema, pokud typ není známý (stejné jako souhlas — nejčastější). */
const FALLBACK_SCHEMA = SOUHLAS_SCHEMA;

export function getContractDocumentPartyFields(
  contractType: string | null | undefined,
): ContractDocumentPartyFieldsSchema {
  const key = (contractType ?? '').trim().toLowerCase();
  if ((ACQUISITION_CONTRACT_TYPES as readonly string[]).includes(key)) {
    return CONTRACT_DOCUMENT_PARTY_FIELDS_BY_TYPE[key as AcquisitionContractType];
  }
  return FALLBACK_SCHEMA;
}

export function isContractPartyOwnerFormFieldVisible(
  contractType: string | null | undefined,
  field: ContractPartyOwnerFormField,
): boolean {
  return getContractDocumentPartyFields(contractType).owners.some((f) => f.key === field);
}

export function isContractPartyParcelFormFieldVisible(
  contractType: string | null | undefined,
  field: ContractPartyParcelFormField,
): boolean {
  return getContractDocumentPartyFields(contractType).parcels.some((f) => f.key === field);
}

/**
 * Přidat / Odebrat parcelu v „Údaje smlouvy".
 * Souhlas + digitální cesta: false (jen parcela budovy).
 */
export function contractTypeAllowsPartyParcelAddRemove(
  contractType: string | null | undefined,
): boolean {
  return !isAcquisitionSingleBuildingParcelContract(contractType);
}

export function contractPartyShowDigitalSignContactHint(
  contractType: string | null | undefined,
): boolean {
  return getContractDocumentPartyFields(contractType).showDigitalSignContactHint;
}

/**
 * Digitální podpis akviziční smlouvy vyžaduje ≥1 kontakt celkově
 * (e-mail nebo telefon u libovolné osoby / legacy sloupců) — viz
 * {@link hasOwnerContactFromRows} v `owner-contact.util`.
 * Parita web / Android / iOS / sign API.
 */
export function contractTypeRequiresOwnerContactForSign(
  contractType: string | null | undefined,
): boolean {
  return contractPartyShowDigitalSignContactHint(contractType);
}

/** Find owner field spec from any schema that defines it (prefer concrete types). */
function findOwnerFieldSpec(
  field: ContractPartyOwnerFormField,
): ContractDocumentPartyFieldSpec<ContractPartyOwnerFormField> | undefined {
  for (const schema of Object.values(CONTRACT_DOCUMENT_PARTY_FIELDS_BY_TYPE)) {
    const hit = schema.owners.find((f) => f.key === field);
    if (hit) return hit;
  }
  return undefined;
}

/** České UI label pro pole vlastníka — z katalogu schémat (`labelCs`). */
export function contractPartyOwnerFormFieldLabelCs(
  field: ContractPartyOwnerFormField,
): string {
  return findOwnerFieldSpec(field)?.labelCs ?? field;
}

/** České nápověda pod polem vlastníka (`hintCs`), nebo null. */
export function contractPartyOwnerFormFieldHintCs(
  field: ContractPartyOwnerFormField,
): string | null {
  return findOwnerFieldSpec(field)?.hintCs?.trim() || null;
}

/** Placeholder inputu vlastníka (`placeholderCs`), nebo null. */
export function contractPartyOwnerFormFieldPlaceholderCs(
  field: ContractPartyOwnerFormField,
): string | null {
  return findOwnerFieldSpec(field)?.placeholderCs?.trim() || null;
}

/** Soft keyboard / input mode pro pole vlastníka. */
export function contractPartyOwnerFormFieldInputMode(
  field: ContractPartyOwnerFormField,
): 'text' | 'numeric' | 'email' | 'tel' {
  return findOwnerFieldSpec(field)?.inputMode ?? 'text';
}

/** České UI label pro pole parcely — z katalogu schémat (`labelCs`). */
export function contractPartyParcelFormFieldLabelCs(
  field: ContractPartyParcelFormField,
): string {
  for (const schema of Object.values(CONTRACT_DOCUMENT_PARTY_FIELDS_BY_TYPE)) {
    const hit = schema.parcels.find((f) => f.key === field);
    if (hit) return hit.labelCs;
  }
  return field;
}
